/**
 * Profil entreprise.
 *
 * Modifier ce profil relance le moteur d'applicabilité : la checklist est
 * recalculée, mais les démarches existantes et les champs que vous avez édités
 * sont préservés.
 */

import { useEffect, useState } from "react";

import { businessApi } from "../api/endpoints";
import type { MissingProfileField } from "../api/types";
import { useToast } from "../components/ToastContext";
import {
  Alert,
  Button,
  Card,
  ErrorAlert,
  type FieldAttention,
  LoadingBlock,
  PageHeader,
  SelectField,
  TextAreaField,
  TextField,
} from "../components/ui";
import { useDocumentTitle, useMutation, useResource } from "../hooks/useApi";
import { centsToEuros, eurosToCents, formatDate } from "../lib/format";

/** Identifiant DOM stable d'un champ du profil, pour pouvoir lui donner le focus. */
const fieldId = (name: string) => `profil-${name}`;

/**
 * Amène le champ concerné à l'écran et lui donne le focus.
 *
 * Le focus, et pas seulement le défilement : sans lui, un lecteur d'écran
 * resterait sur le bandeau et l'utilisateur au clavier devrait retrouver le
 * champ à la main.
 */
function focusField(name: string) {
  const element = document.getElementById(fieldId(name));
  if (!element) return;
  // Le focus d'abord : c'est l'essentiel, et il doit avoir lieu même si le
  // défilement échoue. `scrollIntoView` n'existe pas partout (anciens
  // navigateurs, environnements de test) et lever ici empêcherait le focus.
  element.focus({ preventScroll: true });
  element.scrollIntoView?.({ behavior: "smooth", block: "center" });
}

export function BusinessProfilePage() {
  useDocumentTitle("Profil entreprise");
  const toast = useToast();
  const business = useResource(() => businessApi.current(), []);
  const options = useResource(() => businessApi.options(), []);
  const thresholds = useResource(() => businessApi.vatThresholds(), []);

  const [form, setForm] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const b = business.data;
    if (!b) return;
    setForm({
      trade_name: b.trade_name,
      legal_name: b.legal_name ?? "",
      siren: b.siren ?? "",
      siret: b.siret ?? "",
      ape_code: b.ape_code ?? "",
      created_on: b.created_on ?? "",
      activity_started_on: b.activity_started_on ?? "",
      legal_form: b.legal_form,
      tax_regime: b.tax_regime,
      activity_nature: b.activity_nature,
      main_activity_label: b.main_activity_label ?? "",
      urssaf_periodicity: b.urssaf_periodicity,
      vat_regime: b.vat_regime,
      income_tax_option: b.income_tax_option,
      acre: b.acre,
      has_employees: b.has_employees,
      has_dedicated_bank_account: b.has_dedicated_bank_account,
      has_professional_insurance: b.has_professional_insurance,
      intracommunity_operations: b.intracommunity_operations,
      regulated_activity: b.regulated_activity,
      premises_subject_to_property_tax: b.premises_subject_to_property_tax,
      address_line1: b.address_line1 ?? "",
      postal_code: b.postal_code ?? "",
      city: b.city ?? "",
      notes: b.notes ?? "",
      last_year_revenue_goods: centsToEuros(b.last_year_revenue_goods_cents),
      last_year_revenue_services: centsToEuros(
        b.last_year_revenue_services_cents,
      ),
    });
    setDirty(false);
  }, [business.data]);

  const set = (key: string, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    setDirty(true);
  };

  const save = useMutation(
    () => {
      const payload: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(form)) {
        if (key.startsWith("last_year_revenue_")) continue;
        payload[key] = value === "" ? null : value;
      }
      payload.last_year_revenue_goods_cents = eurosToCents(
        form.last_year_revenue_goods ?? "",
      );
      payload.last_year_revenue_services_cents = eurosToCents(
        form.last_year_revenue_services ?? "",
      );
      return businessApi.update(payload);
    },
    {
      onSuccess: async () => {
        await business.reload();
        await thresholds.reload();
        toast.success(
          "Profil enregistré",
          "Votre checklist a été recalculée. Vos démarches existantes et vos modifications sont conservées.",
        );
      },
      onError: (error) => toast.error("Enregistrement refusé", error.message),
    },
  );

  const regenerate = useMutation(() => businessApi.generateTasks(), {
    onSuccess: (result) => toast.success("Checklist régénérée", result.message),
    onError: (error) => toast.error("Régénération impossible", error.message),
  });

  if (business.loading || options.loading) return <LoadingBlock />;
  if (business.error) {
    return <ErrorAlert error={business.error} title="Profil indisponible" />;
  }
  const b = business.data;
  const o = options.data;
  if (!b || !o) return null;

  const missingByField = new Map<string, MissingProfileField>(
    b.missing_profile_details.map((item) => [item.field, item]),
  );

  /**
   * Mise en évidence d'un champ, s'il figure dans l'analyse du profil.
   *
   * Les deux niveaux restent distincts : un champ « utile » n'est pas présenté
   * comme bloquant, conformément à la distinction exposée par l'API.
   */
  const attentionFor = (name: string): FieldAttention | undefined => {
    const item = missingByField.get(name);
    if (!item || item.importance === "optional") return undefined;
    return {
      level: item.importance,
      label: item.importance_label,
      message: item.why,
    };
  };

  return (
    <>
      <PageHeader
        title="Profil entreprise"
        subtitle={
          <>
            Ces informations déterminent les obligations qui vous concernent.
            Dernière génération de la checklist :{" "}
            {b.rules_generated_at ? formatDate(b.rules_generated_at) : "jamais"}
            .
          </>
        }
        actions={
          <Button
            variant="ghost"
            loading={regenerate.pending}
            onClick={() => void regenerate.run()}
          >
            Régénérer la checklist
          </Button>
        }
      />

      <div className="stack">
        <MissingInformationBanner details={b.missing_profile_details} />
        {(b.siren_checksum_warning || b.siret_checksum_warning) && (
          <Alert tone="warning" title="Numéro à vérifier">
            La clé de contrôle{" "}
            {b.siren_checksum_warning && b.siret_checksum_warning
              ? "du SIREN et du SIRET"
              : b.siren_checksum_warning
                ? "du SIREN"
                : "du SIRET"}{" "}
            ne correspond pas au format habituel. Vérifiez la saisie. Le numéro
            a tout de même été enregistré.
          </Alert>
        )}

        <ErrorAlert error={save.error} title="Enregistrement refusé" />

        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void save.run();
          }}
          noValidate
        >
          <Card title="Identité">
            <TextField
              label="Nom commercial"
              required
              value={form.trade_name ?? ""}
              onChange={(event) => set("trade_name", event.target.value)}
              error={save.fieldErrors.trade_name}
            />
            <div className="grid grid--2">
              <TextField
                label="Raison sociale"
                optional
                value={form.legal_name ?? ""}
                onChange={(event) => set("legal_name", event.target.value)}
              />
              <TextField
                label="Activité principale"
                optional
                value={form.main_activity_label ?? ""}
                onChange={(event) =>
                  set("main_activity_label", event.target.value)
                }
              />
              <TextField
                label="SIREN"
                optional
                inputMode="numeric"
                fieldId={fieldId("siren")}
                attention={attentionFor("siren")}
                value={form.siren ?? ""}
                onChange={(event) => set("siren", event.target.value)}
                error={save.fieldErrors.siren}
              />
              <TextField
                label="SIRET"
                optional
                inputMode="numeric"
                fieldId={fieldId("siret")}
                attention={attentionFor("siret")}
                value={form.siret ?? ""}
                onChange={(event) => set("siret", event.target.value)}
                error={save.fieldErrors.siret}
              />
              <TextField
                label="Code APE"
                optional
                value={form.ape_code ?? ""}
                onChange={(event) => set("ape_code", event.target.value)}
              />
              <TextField
                label="Date de création"
                type="date"
                optional
                fieldId={fieldId("created_on")}
                attention={attentionFor("created_on")}
                value={form.created_on ?? ""}
                onChange={(event) => set("created_on", event.target.value)}
              />
              <TextField
                label="Début d'activité"
                type="date"
                optional
                fieldId={fieldId("activity_started_on")}
                attention={attentionFor("activity_started_on")}
                value={form.activity_started_on ?? ""}
                onChange={(event) =>
                  set("activity_started_on", event.target.value)
                }
              />
            </div>
          </Card>

          <Card title="Régime et obligations">
            <div className="grid grid--2">
              <SelectField
                label="Forme juridique"
                options={o.legal_forms}
                fieldId={fieldId("legal_form")}
                attention={attentionFor("legal_form")}
                value={form.legal_form ?? ""}
                onChange={(event) => set("legal_form", event.target.value)}
              />
              <SelectField
                label="Régime fiscal"
                options={o.tax_regimes}
                fieldId={fieldId("tax_regime")}
                attention={attentionFor("tax_regime")}
                value={form.tax_regime ?? ""}
                onChange={(event) => set("tax_regime", event.target.value)}
              />
              <SelectField
                label="Nature de l'activité"
                options={o.activity_natures}
                hint={o.activity_nature_help ?? undefined}
                fieldId={fieldId("activity_nature")}
                attention={attentionFor("activity_nature")}
                value={form.activity_nature ?? ""}
                onChange={(event) => set("activity_nature", event.target.value)}
              />
              <SelectField
                label="Périodicité Urssaf"
                options={o.urssaf_periodicities}
                fieldId={fieldId("urssaf_periodicity")}
                attention={attentionFor("urssaf_periodicity")}
                value={form.urssaf_periodicity ?? ""}
                onChange={(event) =>
                  set("urssaf_periodicity", event.target.value)
                }
              />
              <SelectField
                label="Régime de TVA"
                options={o.vat_regimes}
                fieldId={fieldId("vat_regime")}
                attention={attentionFor("vat_regime")}
                value={form.vat_regime ?? ""}
                onChange={(event) => set("vat_regime", event.target.value)}
              />
              <SelectField
                label="Option fiscale"
                options={o.income_tax_options}
                fieldId={fieldId("income_tax_option")}
                attention={attentionFor("income_tax_option")}
                value={form.income_tax_option ?? ""}
                onChange={(event) =>
                  set("income_tax_option", event.target.value)
                }
              />
            </div>
          </Card>

          <Card title="Situation">
            <div className="grid grid--2">
              <SelectField
                label="ACRE"
                options={o.tri_states}
                fieldId={fieldId("acre")}
                attention={attentionFor("acre")}
                value={form.acre ?? ""}
                onChange={(event) => set("acre", event.target.value)}
              />
              <SelectField
                label="Salariés"
                options={o.tri_states}
                fieldId={fieldId("has_employees")}
                attention={attentionFor("has_employees")}
                value={form.has_employees ?? ""}
                onChange={(event) => set("has_employees", event.target.value)}
              />
              <SelectField
                label="Compte bancaire dédié"
                options={o.tri_states}
                fieldId={fieldId("has_dedicated_bank_account")}
                attention={attentionFor("has_dedicated_bank_account")}
                value={form.has_dedicated_bank_account ?? ""}
                onChange={(event) =>
                  set("has_dedicated_bank_account", event.target.value)
                }
              />
              <SelectField
                label="Assurance professionnelle"
                options={o.tri_states}
                fieldId={fieldId("has_professional_insurance")}
                attention={attentionFor("has_professional_insurance")}
                value={form.has_professional_insurance ?? ""}
                onChange={(event) =>
                  set("has_professional_insurance", event.target.value)
                }
              />
              <SelectField
                label="Opérations intracommunautaires"
                options={o.tri_states}
                fieldId={fieldId("intracommunity_operations")}
                attention={attentionFor("intracommunity_operations")}
                value={form.intracommunity_operations ?? ""}
                onChange={(event) =>
                  set("intracommunity_operations", event.target.value)
                }
              />
              <SelectField
                label="Activité réglementée"
                options={o.tri_states}
                fieldId={fieldId("regulated_activity")}
                attention={attentionFor("regulated_activity")}
                value={form.regulated_activity ?? ""}
                onChange={(event) =>
                  set("regulated_activity", event.target.value)
                }
              />
              <SelectField
                label="Local soumis à la taxe foncière"
                options={o.tri_states}
                fieldId={fieldId("premises_subject_to_property_tax")}
                attention={attentionFor("premises_subject_to_property_tax")}
                value={form.premises_subject_to_property_tax ?? ""}
                onChange={(event) =>
                  set("premises_subject_to_property_tax", event.target.value)
                }
              />
            </div>
          </Card>

          <Card title="Adresse">
            <TextField
              label="Adresse"
              optional
              value={form.address_line1 ?? ""}
              onChange={(event) => set("address_line1", event.target.value)}
            />
            <div className="grid grid--2">
              <TextField
                label="Code postal"
                optional
                value={form.postal_code ?? ""}
                onChange={(event) => set("postal_code", event.target.value)}
              />
              <TextField
                label="Ville"
                optional
                value={form.city ?? ""}
                onChange={(event) => set("city", event.target.value)}
              />
            </div>
          </Card>

          <Card
            title="Chiffre d'affaires de référence"
            hint="Saisi par vous, utilisé uniquement pour les alertes de seuil. Aucun calcul fiscal n'est effectué."
          >
            <div className="grid grid--2">
              <TextField
                label="CA de l'année précédente — ventes et hébergement"
                optional
                inputMode="decimal"
                value={form.last_year_revenue_goods ?? ""}
                onChange={(event) =>
                  set("last_year_revenue_goods", event.target.value)
                }
                hint="En euros. Exemple : 12500,00"
              />
              <TextField
                label="CA de l'année précédente — prestations de services"
                optional
                inputMode="decimal"
                value={form.last_year_revenue_services ?? ""}
                onChange={(event) =>
                  set("last_year_revenue_services", event.target.value)
                }
                hint="En euros."
              />
            </div>
            {thresholds.data && (
              <Alert
                tone={thresholds.data.alerts.length > 0 ? "warning" : "info"}
                title="Suivi des seuils de TVA"
              >
                <p>{thresholds.data.message}</p>
                {thresholds.data.alerts.length > 0 && (
                  <ul className="mb-0">
                    {thresholds.data.alerts.map((alert) => (
                      <li key={alert}>{alert}</li>
                    ))}
                  </ul>
                )}
                {thresholds.data.source_url && (
                  <p className="mb-0 inline-note">
                    Source :{" "}
                    <a
                      href={thresholds.data.source_url}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {thresholds.data.source_name}
                    </a>
                    {thresholds.data.source_checked_on
                      ? ` — vérifiée le ${formatDate(thresholds.data.source_checked_on)}`
                      : ""}
                  </p>
                )}
              </Alert>
            )}
          </Card>

          <Card title="Notes">
            <TextAreaField
              label="Notes internes"
              optional
              value={form.notes ?? ""}
              onChange={(event) => set("notes", event.target.value)}
            />
          </Card>

          <div className="btn-group">
            <Button
              type="submit"
              variant="primary"
              loading={save.pending}
              disabled={!dirty}
            >
              Enregistrer le profil
            </Button>
            {dirty && (
              <Button
                variant="ghost"
                type="button"
                onClick={() => void business.reload()}
              >
                Annuler mes changements
              </Button>
            )}
          </div>
        </form>
      </div>
    </>
  );
}

/**
 * Bandeau « Informations manquantes ».
 *
 * Trois exigences de la V1.1 : distinguer les niveaux d'impact, expliquer la
 * conséquence précise de chaque absence, et permettre d'atteindre le champ d'un
 * clic. Les éléments sont de vrais boutons, donc accessibles au clavier.
 */
function MissingInformationBanner({
  details,
}: {
  details: MissingProfileField[];
}) {
  const blocking = details.filter((d) => d.importance === "blocking");
  const limiting = details.filter((d) => d.importance === "limiting");
  if (blocking.length === 0 && limiting.length === 0) return null;

  return (
    <Alert
      tone={blocking.length > 0 ? "warning" : "info"}
      title={
        blocking.length > 0
          ? "Informations nécessaires pour déterminer vos obligations"
          : "Informations qui affineraient votre checklist"
      }
    >
      {blocking.length > 0 && (
        <>
          <p>
            Sans ces informations, des obligations ne peuvent pas être
            déterminées. Laisser « Inconnu » reste possible : l'application le
            signalera plutôt que d'affirmer une obligation à tort.
          </p>
          <ul className="missing-list">
            {blocking.map((item) => (
              <li key={item.field}>
                <button
                  type="button"
                  className="missing-list__link"
                  onClick={() => focusField(item.field)}
                >
                  {item.label}
                </button>
                <span className="missing-list__why">{item.why}</span>
                {item.blocked_rules.length > 0 && (
                  <span className="missing-list__rules">
                    Démarches concernées :{" "}
                    {item.blocked_rules.map((rule) => rule.title).join(" ; ")}.
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {limiting.length > 0 && (
        <p className="mb-0 inline-note">
          Rien n'est bloqué, mais ces informations affineraient la liste :{" "}
          {limiting.map((item, index) => (
            <span key={item.field}>
              {index > 0 && ", "}
              <button
                type="button"
                className="missing-list__link"
                onClick={() => focusField(item.field)}
              >
                {item.label}
              </button>
            </span>
          ))}
          .
        </p>
      )}
    </Alert>
  );
}
